import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Stack,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import { Business, Save } from '@mui/icons-material';
import { sponsorService, CreateSponsorRequest, UpdateSponsorRequest } from '../services/sponsorService';
import { AppHeader } from '../components/AppHeader';

  const SponsorForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    logoUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [error, setError] = useState<string>('');

  const fetchSponsor = useCallback(async () => {
    try {
      setInitialLoading(true);
      const sponsor = await sponsorService.getSponsorById(id!);
      setFormData({
        name: sponsor.name,
        logoUrl: sponsor.logoUrl || ''
      });
    } catch (error) {
      console.error('Erro ao carregar patrocinador:', error);
      setError('Erro ao carregar dados do patrocinador');
    } finally {
      setInitialLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing && id) {
      fetchSponsor();
    }
  }, [id, isEditing, fetchSponsor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditing) {
        const updateData: UpdateSponsorRequest = {
          name: formData.name.trim(),
          logoUrl: formData.logoUrl.trim() || undefined
        };
        await sponsorService.updateSponsor(id!, updateData);
      } else {
        const createData: CreateSponsorRequest = {
          name: formData.name.trim(),
          logoUrl: formData.logoUrl.trim() || undefined
        };
        await sponsorService.createSponsor(createData);
      }
      navigate('/sponsors');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar patrocinador');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <>
      <AppHeader 
        title={isEditing ? 'Editar Patrocinador' : 'Novo Patrocinador'}
        subtitle={isEditing 
          ? 'Atualize as informações do patrocinador'
          : 'Cadastre um novo patrocinador para suas campanhas'
        }
        icon={<Business />}
        backTo="/sponsors"
      />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, elevation: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            {isEditing ? 'Editar Patrocinador' : 'Novo Patrocinador'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Preview do Logo */}
              {formData.logoUrl && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Avatar
                    src={formData.logoUrl}
                    alt="Preview do Logo"
                    sx={{ width: 80, height: 80 }}
                  >
                    <Business sx={{ fontSize: 40 }} />
                  </Avatar>
                </Box>
              )}

              <TextField
                fullWidth
                label="Nome do Patrocinador"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                sx={{ borderRadius: 2 }}
              />

              <TextField
                fullWidth
                label="URL do Logo (opcional)"
                value={formData.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                placeholder="https://exemplo.com/logo.png"
                sx={{ borderRadius: 2 }}
              />

              {/* Botões de Ação */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/sponsors')}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !formData.name.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  {loading ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar Patrocinador')}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Container>
    </>
  );
}; 
export default SponsorForm;